import moment from 'moment'
import { v4 } from 'uuid'

import { computeAspectRatio, fileStatus, inferPlacementType } from './media'

export const mergeCreativeMedia = (creative = {}) => [
  ...(creative.media || []),
  ...(creative.placement_media || []),
]

// Drops fields from the old dual-array model and one-off insert hints.
// Used on load/migration so state and payload only keep the flat media[] shape.
const stripLegacyMediaFields = (file = {}) => {
  const rest = { ...file }
  delete rest.placement_group_id // legacy link between media[] and placement_media[]
  delete rest.source // legacy split: 'media' vs 'placement_media'
  delete rest.insertAfterId // transient hint for addFile only, never persisted
  return rest
}

export const normalizeIncomingMediaItem = (file, index) => {
  // A YouTube video item (see mapYoutubeVideoForPayload) isn't a real
  // upload — it has no file/ratio/placement concept, and paths that read
  // `setup.creatives[].media` generically (not every caller splits youtube
  // items out first, unlike CampaignForm's parseCreatives) would otherwise
  // corrupt it: FB-only fields get bolted on and youtube_video_id survives
  // only by accident of the spread below, not by design. Pass it through
  // untouched instead.
  if (file.youtube_video_id) {
    return stripLegacyMediaFields({ ...file, id: file.id || v4() })
  }

  const normalizedId = file.id || v4()
  const type = file.type ?? ''
  const ratio =
    file.ratio ??
    (file.width && file.height && type.startsWith('image/')
      ? computeAspectRatio(file.width, file.height)
      : null)

  return stripLegacyMediaFields({
    ...file,
    id: normalizedId,
    status: file.status ?? fileStatus.success,
    index,
    filename: file.filename || file.name,
    headline: file.headline ?? null,
    ratio,
    placement_type: file.placement_type ?? inferPlacementType(file),
    sub_group_id: file.sub_group_id ?? null,
    is_generated: file.is_generated ?? false,
    generating: file.generating ?? false,
  })
}

// Every card/group uses a stable shared UUID as sub_group_id.
// Defensive fallback for any item that arrives without one.
export const ensureSharedSubGroupIds = (media = []) => {
  if (!Array.isArray(media) || !media.length) {
    return []
  }

  return media.map((item) =>
    item.youtube_video_id || item.sub_group_id != null ? item : { ...item, sub_group_id: v4() },
  )
}

export const augmentIncomingMedia = (media) => {
  if (!media) {
    return []
  }

  return ensureSharedSubGroupIds(media.map(normalizeIncomingMediaItem))
}

const assignLegacySubGroups = (items = []) => {
  const subGroupByPlacementGroupId = new Map()
  const result = []

  items.forEach((file) => {
    if (file.sub_group_id) {
      result.push(file)
      return
    }

    const placementGroupId = file.placement_group_id

    if (!placementGroupId) {
      result.push({ ...file, sub_group_id: v4() })
      return
    }

    if (!subGroupByPlacementGroupId.has(placementGroupId)) {
      subGroupByPlacementGroupId.set(placementGroupId, v4())
    }

    result.push({
      ...file,
      sub_group_id: subGroupByPlacementGroupId.get(placementGroupId),
    })
  })

  return result
}

export const mergeLegacyCreativeMedia = (creative = {}) => {
  const media = [...(creative.media || [])]
  const placement_media = [...(creative.placement_media || [])]

  if (!placement_media.length) {
    return media.map((file) => stripLegacyMediaFields(file))
  }

  const anchorByPlacementGroupId = new Map()

  media.forEach((file, index) => {
    if (file.placement_group_id && !anchorByPlacementGroupId.has(file.placement_group_id)) {
      anchorByPlacementGroupId.set(file.placement_group_id, { anchorId: file.id, index })
    }
  })

  const placementsByAnchorId = new Map()
  const orphanPlacements = []

  placement_media.forEach((file) => {
    const anchorId = file.placement_group_id
      ? (anchorByPlacementGroupId.get(file.placement_group_id)?.anchorId ?? null)
      : null

    const migrated = {
      ...stripLegacyMediaFields(file),
      sub_group_id: file.sub_group_id ?? null,
      placement_group_id: file.placement_group_id ?? null,
    }

    if (!anchorId) {
      orphanPlacements.push(migrated)
      return
    }

    if (!placementsByAnchorId.has(anchorId)) {
      placementsByAnchorId.set(anchorId, [])
    }

    placementsByAnchorId.get(anchorId).push(migrated)
  })

  const merged = []

  media.forEach((file) => {
    const placements = placementsByAnchorId.get(file.id)

    if (!placements?.length) {
      merged.push(stripLegacyMediaFields(file))
      return
    }

    const withSubGroups = assignLegacySubGroups(placements)
    const batchesBySubGroup = new Map()

    withSubGroups.forEach((item) => {
      if (!batchesBySubGroup.has(item.sub_group_id)) {
        batchesBySubGroup.set(item.sub_group_id, [])
      }

      batchesBySubGroup.get(item.sub_group_id).push(item)
    })

    const batches = [...batchesBySubGroup.values()]
    const ratioBatches = batches.filter((batch) => batch.every((item) => item.headline == null))
    const headlineBatches = batches.filter((batch) => batch.some((item) => item.headline != null))

    // Anchor shares sub_group_id with its ratio-variant batch so they render as one card.
    const anchorSubGroupId = ratioBatches.length === 1 ? ratioBatches[0][0].sub_group_id : null

    merged.push({
      ...stripLegacyMediaFields(file),
      ...(anchorSubGroupId ? { sub_group_id: anchorSubGroupId } : {}),
    })
    merged.push(...ratioBatches.flat(), ...headlineBatches.flat())
  })

  const orphanWithSubGroups = assignLegacySubGroups(orphanPlacements)
  merged.push(...orphanWithSubGroups)

  return merged.map(stripLegacyMediaFields)
}

export const augmentIncomingCreativeMedia = (creative = {}) =>
  augmentIncomingMedia(mergeLegacyCreativeMedia(creative))

export const findLastSubGroupIndex = (media = [], subGroupId) => {
  if (!subGroupId) {
    return -1
  }

  let lastIndex = -1

  media.forEach((item, index) => {
    if (item.sub_group_id === subGroupId) {
      lastIndex = index
    }
  })

  return lastIndex
}

export const findGroupBlockEndIndexByAnchor = (media = [], anchorId) => {
  if (!anchorId) {
    return -1
  }

  const anchorIndex = media.findIndex((item) => item.id === anchorId)

  if (anchorIndex < 0) {
    return -1
  }

  const anchor = media[anchorIndex]
  const anchorSubGroupId = anchor.sub_group_id ?? anchor.id

  return findLastSubGroupIndex(media, anchorSubGroupId)
}

export const resolveMediaInsertIndex = (media = [], file = {}) => {
  if (!file.sub_group_id) {
    return media.length
  }

  const existingSubGroupIndex = findLastSubGroupIndex(media, file.sub_group_id)

  if (existingSubGroupIndex >= 0) {
    return existingSubGroupIndex + 1
  }

  if (!file.group_id) {
    return media.length
  }

  const anchorGroupEndIndex = findGroupBlockEndIndexByAnchor(media, file.group_id)
  if (anchorGroupEndIndex < 0) {
    return media.length
  }

  return anchorGroupEndIndex + 1
}

export const insertMediaItems = (media = [], newFiles = []) => {
  let result = [...media]

  newFiles.forEach((file) => {
    // group_id is only a positioning hint (find the anchor to insert next to) — never stored.
    const { insertAfterId, group_id, ...cleanFile } = file
    void group_id
    let insertIndex = resolveMediaInsertIndex(result, file)

    if (insertAfterId) {
      const afterIndex = result.findIndex((item) => item.id === insertAfterId)
      insertIndex = afterIndex >= 0 ? afterIndex + 1 : result.length
    }

    result = [...result.slice(0, insertIndex), cleanFile, ...result.slice(insertIndex)]
  })

  return result
}

export const clearGroupIdForRemovedOriginal = (media = [], removedOriginalId) =>
  media.map((file) => (file.group_id === removedOriginalId ? { ...file, group_id: null } : file))

// Google video ads reference the source video by ID only — `url` is
// deliberately omitted even though YouTubeUrlInput's onVideo payload carries one.
export const mapYoutubeVideoForPayload = (video) => ({
  id: video.id || v4(),
  type: 'video/youtube',
  youtube_video_id: video.youtube_video_id,
  filename: video.title || 'YouTube video',
})

export const mapMediaItemForPayload = (file) => {
  const item = {
    id: file.id,
    type: file.type,
    url: file.url,
    filename: file.name || file.filename || `unnamed_file_${moment().unix()}`,
    ...(file.size && { size: file.size || 0 }),
  }

  if (file.width && file.height) {
    item.width = file.width
    item.height = file.height
  }

  if (file.ratio) {
    item.ratio = file.ratio
  }

  if (file.sub_group_id) {
    item.sub_group_id = file.sub_group_id
  }

  if (file.headline) {
    item.headline = file.headline
  }

  item.is_generated = typeof file.is_generated === 'boolean' ? file.is_generated : false
  item.generating = typeof file.generating === 'boolean' ? file.generating : false

  return item
}
